import { criterionDraftChangesNode } from "@repo/app-types/entities/configurationAgent";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type {
  PlaybookCriterionNode,
  PlaybookId,
} from "@repo/app-types/entities/playbook";
import type { CriteriaProposalDetail } from "@repo/app-types/entities/playbookProposalDetail";
import type { PublishNodeDisclosure } from "@repo/app-types/helpers/playbookNodePublish";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconSparkles, IconTrash } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  isCriterionSampleToolResultEntry,
  isRemovalSampleToolResultEntry,
  type SampleToolResultCriterionEntry,
  type SampleToolResultEntry,
} from "../../hooks/useSampleToolResults.js";
import { CopilotProposalBanner } from "./copilotProposalBanner.js";
import { CriteriaForm } from "./criteriaForm.js";
import { PublishNodeConfirmationModal } from "./publishPlaybookPartialChangeConfirmationModal.js";
import { SuggestionResults } from "./suggestionResults.js";

export interface CriteriaSettingsProps {
  criteria: PlaybookCriterionNode;
  /** Live (published) version of this criterion, if any, for showing field-level diffs. */
  liveCriteria?: PlaybookCriterionNode;
  /**
   * Called on in-form edits (typing in the Name field, etc.). Optional —
   * suggestion mode renders the form read-only and never invokes this,
   * and ghost criteria don't need an in-place edit path.
   */
  onUpdateCriterion?: (criteria: PlaybookCriterionNode) => void;
  isReadOnly?: boolean;
  /**
   * Copilot's unresolved proposal for this criterion, if any — a node holds
   * one. Its own action decides the render: a `delete` shows the removal
   * banner over an untouched form, an edit shows the merged criterion
   * read-only with the Results pane, and a check-only run (a draft that
   * restates the criterion) shows Results with no banner at all.
   */
  proposal?: SampleToolResultEntry;
  /**
   * Accept the proposal. For an edit this carries the criterion as it would
   * stand once the draft is merged in — what the caller writes to the tree.
   * A removal carries nothing, having nothing to write.
   */
  onApplyProposal?: (mergedCriteria?: PlaybookCriterionNode) => void;
  /** Reject it, leaving the criterion as it is. */
  onDiscardProposal?: () => void;
  /** Outcome definition for rendering the comparison card copy. */
  outcomeDefinition?: OutcomeDefinition | null;
  /** Hard-delete this criterion from the playbook tree. Only invoked from the editable-mode footer. */
  onDelete?: () => void;
  /** Ask the AI assistant to optimize this criterion. */
  onOptimizeCriterion?: (criterion: PlaybookCriterionNode) => void;
  /** Ask the AI assistant to run the sample-distribution tool against this criterion. */
  onTestCriterion?: (criterion: PlaybookCriterionNode) => void;
  /** Whether an assistant run is in progress (gates Optimize/Test). */
  isOptimizingSectionOrCriterion?: boolean;
  /** Absent where inline publishing isn't offered. */
  onPublishNode?: () => void;
  /** What a publish would and wouldn't carry; gates the consent modal. */
  publishDisclosure?: PublishNodeDisclosure;
  /** This node differs from live; otherwise the Publish button explains why it's disabled. */
  hasChangesToPublish?: boolean;
  /** Another publish or discard is in flight. */
  isPublishBlocked?: boolean;
  isPublishing?: boolean;
  playbookId?: PlaybookId | null;
}

export const CriteriaSettings: FC<CriteriaSettingsProps> = ({
  criteria,
  liveCriteria,
  onUpdateCriterion,
  isReadOnly = false,
  proposal,
  onApplyProposal,
  onDiscardProposal,
  outcomeDefinition,
  onDelete,
  onOptimizeCriterion,
  onTestCriterion,
  isOptimizingSectionOrCriterion = false,
  onPublishNode,
  publishDisclosure,
  hasChangesToPublish = false,
  isPublishBlocked = false,
  isPublishing,
  playbookId,
}) => {
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
  // Consent is asked only when there's something to disclose: the publish
  // pulls in a new section, or the draft holds changes it can't carry.
  const publishNeedsConfirmation =
    publishDisclosure !== undefined &&
    (publishDisclosure.newAncestorSectionCount > 0 ||
      publishDisclosure.globalOnlyChanges.length > 0);

  const updateCriteria = (updates: Partial<PlaybookCriterionNode>) => {
    onUpdateCriterion?.({ ...criteria, ...updates });
  };

  // A removal has no draft and no measurements; an edit has both, and drives
  // the preview and the Results pane below.
  const isRemovalProposal =
    proposal !== undefined && isRemovalSampleToolResultEntry(proposal);
  const suggestion =
    proposal !== undefined && isCriterionSampleToolResultEntry(proposal)
      ? proposal
      : undefined;

  const isSuggestionMode =
    suggestion !== undefined &&
    isCriterionInSuggestionMode(criteria, suggestion);

  // Show the Results pane whenever there are measurements to show — a proposed
  // edit, or a check-only run that measured the criterion without changing it.
  const hasResults = suggestion !== undefined;

  // Read-only mode (e.g. version-history view): keep the historical
  // single-column form, no Results pane.
  if (isReadOnly) {
    return (
      <div className="space-y-4 p-2">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">Criteria</h3>
        </div>
        <CriteriaForm
          criteria={criteria}
          liveCriteria={liveCriteria}
          onUpdateCriterion={updateCriteria}
          isReadOnly
        />
      </div>
    );
  }

  const displayCriteria = isSuggestionMode
    ? mergeSuggestionIntoCriteria(criteria, suggestion.candidate_draft)
    : criteria;
  const diffBaseline = isSuggestionMode ? criteria : liveCriteria;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {isRemovalProposal ? (
        <CopilotProposalBanner
          message="Copilot suggests removing this criterion."
          isDestructive
          discardLabel="Keep it"
          confirmLabel="Remove criterion"
          onDiscard={() => onDiscardProposal?.()}
          onConfirm={() => onApplyProposal?.()}
        />
      ) : null}
      {isSuggestionMode ? (
        <CopilotProposalBanner
          message="Showing changes Copilot suggests."
          discardLabel="Discard changes"
          confirmLabel="Apply changes"
          onDiscard={() => onDiscardProposal?.()}
          onConfirm={() => onApplyProposal?.(displayCriteria)}
        />
      ) : null}
      <div
        className={twMerge(
          "grid min-h-0 flex-1 grid-cols-1 gap-x-2",
          hasResults && "md:grid-cols-[1fr_1px_1fr]",
        )}
      >
        <div className="flex min-h-0 flex-col p-2">
          <div className="flex min-h-0 flex-1 flex-col space-y-4">
            <h3 className="text-base font-semibold text-foreground">
              Criteria
            </h3>
            <CriteriaForm
              criteria={displayCriteria}
              liveCriteria={diffBaseline}
              onUpdateCriterion={updateCriteria}
              isReadOnly={isSuggestionMode}
            />
          </div>
          {!isSuggestionMode ? (
            <div className="mt-auto flex items-center justify-between pt-4">
              <div className="flex items-center gap-2">
                {onDelete ? (
                  <IconButton
                    variant={ButtonVariant.Warning}
                    size={Size.MEDIUM}
                    icon={<IconTrash className="size-4" />}
                    accessibleLabel="Delete"
                    onClick={onDelete}
                  />
                ) : null}
                {onOptimizeCriterion ? (
                  <Button
                    variant={ButtonVariant.Secondary}
                    size={Size.MEDIUM}
                    icon={<IconSparkles className="size-4" />}
                    onClick={() => onOptimizeCriterion(criteria)}
                    isDisabled={
                      isOptimizingSectionOrCriterion || !criteria.name.trim()
                    }
                  >
                    Optimize
                  </Button>
                ) : null}
                {onTestCriterion ? (
                  <Button
                    variant={ButtonVariant.Secondary}
                    size={Size.MEDIUM}
                    onClick={() => onTestCriterion(criteria)}
                    isDisabled={
                      isOptimizingSectionOrCriterion || !criteria.name.trim()
                    }
                  >
                    Test
                  </Button>
                ) : null}
              </div>
              {onPublishNode ? (
                // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
                <TooltipTrigger isDisabled={hasChangesToPublish}>
                  <TooltipHoverTarget
                    shape="rounded"
                    isDisabled={hasChangesToPublish}
                  >
                    <Button
                      variant={ButtonVariant.Primary}
                      size={Size.MEDIUM}
                      onClick={
                        publishNeedsConfirmation
                          ? () => setShowPublishConfirmation(true)
                          : onPublishNode
                      }
                      isDisabled={isPublishBlocked || !hasChangesToPublish}
                      isLoading={isPublishing}
                    >
                      Publish this criteria
                    </Button>
                  </TooltipHoverTarget>
                  <SimpleTooltip>No changes to publish</SimpleTooltip>
                </TooltipTrigger>
              ) : null}
              {onPublishNode && publishDisclosure ? (
                <PublishNodeConfirmationModal
                  isOpen={showPublishConfirmation}
                  onOpenChange={setShowPublishConfirmation}
                  nodeLabel="criteria"
                  disclosure={publishDisclosure}
                  onConfirm={onPublishNode}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        {hasResults ? (
          <>
            <div
              aria-hidden
              className={twMerge(
                "-mb-2 hidden bg-gray-200 md:block",
                !isSuggestionMode && "-mt-2",
              )}
            />
            <div className="flex min-h-0 flex-col gap-4 p-2">
              <h3 className="shrink-0 text-base font-semibold text-foreground">
                Results
              </h3>
              <div className="min-h-0 flex-1 overflow-y-auto p-1">
                <SuggestionResults
                  aggregates={suggestion?.aggregates}
                  outcomeDefinition={outcomeDefinition ?? undefined}
                  suggestion={suggestion}
                  playbookId={playbookId}
                  playbookNodeId={displayCriteria.id}
                  gradingValueType={displayCriteria.grading_value_type}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

/**
 * For an existing-criterion edit suggestion, merge populated `candidate_draft`
 * fields into the current criterion so the read-only form renders the
 * proposed end-state.
 */
function mergeSuggestionIntoCriteria(
  current: PlaybookCriterionNode,
  candidateDraft: CriteriaProposalDetail["candidate_draft"],
): PlaybookCriterionNode {
  return {
    ...current,
    ...(candidateDraft.name !== undefined ? { name: candidateDraft.name } : {}),
    ...(candidateDraft.criteriaType !== undefined
      ? { grading_value_type: candidateDraft.criteriaType }
      : {}),
    ...(candidateDraft.gradingContext !== undefined
      ? { grading_context: candidateDraft.gradingContext }
      : {}),
  };
}

/**
 * For a `create_criterion` ghost proposal, synthesize a full
 * `PlaybookCriterionNode` from the saved `candidate_draft`. Callers can pass
 * the result as the `criteria` prop alongside `suggestion` to render the
 * ghost in the same `CriteriaSettings` shell.
 */
export function buildNewCriterionFromSuggestion(
  suggestion: SampleToolResultCriterionEntry,
): PlaybookCriterionNode {
  const { candidate_draft } = suggestion;
  return {
    type: "criteria",
    id: suggestion.playbook_node_id,
    name: candidate_draft.name ?? "",
    description: candidate_draft.description ?? "",
    grading_context: candidate_draft.gradingContext ?? "",
    grading_value_type: candidate_draft.criteriaType ?? "1_5_score",
  };
}

/**
 * True when the criterion should render with the suggestion banner + read-only
 * form + Results pane. A "check-only" run — where the agent re-tested the
 * criterion without proposing edits — does not count.
 */
export function isCriterionInSuggestionMode(
  criterion: PlaybookCriterionNode,
  suggestion: SampleToolResultCriterionEntry | undefined,
): boolean {
  if (!suggestion) return false;
  if (suggestion.kind === "create_criterion") return true;
  return criterionDraftChangesNode(criterion, suggestion.candidate_draft);
}
